import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import * as z from "zod";
import { NotFoundError, type __BASE_ERROR } from "./errors";
import { isErr, type Failure, type Result } from "./result";

export function isInArray<T, const A extends readonly T[]>(
  item: T,
  array: A,
): item is A[number] {
  return array.includes(item as A[number]);
}

export function errorMessage(
  requiredMessage = "This field is required",
  invalidTypeMessage = "This field must be a string",
): { error: (issue: { input: unknown }) => string } {
  return {
    error: (issue) => {
      if (
        issue.input === undefined ||
        issue.input === null ||
        issue.input === ""
      ) {
        return requiredMessage;
      }
      return invalidTypeMessage;
    },
  };
}

export const phoneSchema = (...params: Parameters<typeof errorMessage>) =>
  z.string().refine(
    (value) => {
      const phone = parsePhoneNumberFromString(value);
      return phone?.isValid() ?? false;
    },
    errorMessage(...params),
  );

export function isNotFoundError<T>(
  error: Result<T, __BASE_ERROR>,
): error is Failure<NotFoundError> {
  return isErr(error) && error.error instanceof NotFoundError;
}

export function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

export function base64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function inferImageMimeTypeFromBase64(base64: string): string {
  const s = base64.trim();
  // PNG: 89 50 4E 47
  if (s.startsWith("iVBORw0KGgo")) return "image/png";
  // JPEG: FF D8 FF
  if (s.startsWith("/9j/")) return "image/jpeg";
  // GIF: 47 49 46
  if (s.startsWith("R0lGOD")) return "image/gif";
  // WebP: RIFF....WEBP
  if (s.startsWith("UklGR")) return "image/webp";
  // Default to JPEG
  return "image/jpeg";
}

export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export type TPayment = { id: string; transactionId: string; amount: number };

/**
 * Find the best subset of payments that sum to the target amount.
 * @param target - The target amount to reach.
 * @param payments - The payments to choose from.
 *
 * @returns The IDs of the payments that sum to the target amount.
 */
export function findBestSubset(target: number, payments: TPayment[]): string[] {
  let dp = new Map<number, string[]>();
  dp.set(0, []);

  for (const { id, amount } of payments) {
    for (const [sum, ids] of Array.from(dp)) {
      const nextSum = sum + amount;

      if (nextSum <= target && !dp.has(nextSum)) {
        dp.set(nextSum, [...ids, id]);
      }
    }
  }

  const allSums = Array.from(dp.keys());
  const maxSum = Math.max(...allSums);

  return dp.get(maxSum) || [];
}

export function camelToSnake(key: string): string {
  return key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

export function keysCamelToSnake<T>(value: T): any {
  if (Array.isArray(value)) {
    return value.map(keysCamelToSnake);
  }

  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [
        camelToSnake(k),
        keysCamelToSnake(v),
      ]),
    );
  }

  return value;
}
